import { type ReactNode, useState } from "react";
import type { TradeDetails } from "../../api/types";
import { HoverTip } from "../HoverTip/HoverTip";
import { formatMoney, formatPnlMoney, formatTime, formatTradeId } from "../../utils/format";
import { copyToClipboard } from "../../utils/clipboard";

// Full single-trade detail layout — extracted from Trades.tsx's own aside
// panel (Trades.tsx originally had this markup inline) so it can be reused
// verbatim wherever "детали по сделке в том виде, в котором они показываются
// на вкладке Сделки" is needed elsewhere — currently Trades.tsx's own aside
// AND Overview.tsx's compact "Открытые позиции" hover tooltip (user
// request: hovering an open position there should show this exact panel,
// not a re-derived summary that can drift out of sync with the real thing).
// Self-contained copy-to-clipboard state (own useState, not a prop) — the
// two call sites don't share a "которая карточка ID сейчас скопирована"
// concept, each hover/selection is independent.
export function TradeDetailPanel({ details }: { details: TradeDetails }) {
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopyTradeId = async (tradeId: string) => {
    const ok = await copyToClipboard(tradeId);
    if (ok) {
      setCopiedId(tradeId);
      setTimeout(() => setCopiedId((c) => (c === tradeId ? null : c)), 1500);
    }
  };

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
        <span
          style={{
            font: "700 10.5px 'Golos Text',sans-serif",
            letterSpacing: ".05em",
            padding: "2px 7px",
            borderRadius: 5,
            background: details.direction === "LONG" ? "var(--longsoft)" : "var(--shortsoft)",
            color: details.direction === "LONG" ? "var(--long)" : "var(--short)",
          }}
        >
          {details.direction}
        </span>
        <span
          title={copiedId === details.trade_id ? "Скопировано!" : `${details.trade_id} · клик — копировать`}
          onClick={() => handleCopyTradeId(details.trade_id)}
          style={{
            font: "700 17px 'JetBrains Mono',monospace",
            cursor: "pointer",
            color: copiedId === details.trade_id ? "var(--long)" : undefined,
          }}
        >
          {copiedId === details.trade_id ? "Скопировано!" : formatTradeId(details.trade_id)}
        </span>
        <span
          style={{
            font: "700 17px 'JetBrains Mono',monospace",
            color: parseFloat(details.net_pnl) >= 0 ? "var(--long)" : "var(--short)",
          }}
        >
          {formatPnlMoney(details.net_pnl)}
        </span>
      </div>
      <div
        title="клик — копировать"
        onClick={() => handleCopyTradeId(details.trade_id)}
        style={{ font: "400 11px 'JetBrains Mono',monospace", color: "var(--fg3)", marginTop: 5, wordBreak: "break-all", cursor: "pointer" }}
      >
        {copiedId === details.trade_id ? "Скопировано!" : details.trade_id}
      </div>

      <DetailBlock title="Параметры">
        <DetailRow label="Бот" value={details.bot_instance_id} />
        <DetailRow label="Цена входа" value={formatMoney(details.entry_price)} />
        <DetailRow label="Цена выхода" value={details.exit_price ? formatMoney(details.exit_price) : "—"} />
        <DetailRow label="Размер" value={`${details.size} BTC`} />
        <DetailRow label="Статус" value={details.status} valueColor={details.status === "CLOSED" ? "var(--fg2)" : "var(--warn)"} />
        <DetailRow label="Открытие" value={formatTime(details.opened_at, true)} />
        <DetailRow label="Закрытие" value={details.closed_at ? formatTime(details.closed_at, true) : "—"} />
      </DetailBlock>

      <DetailBlock title="Экономика сделки">
        <DetailRow label="Капитал в риске" value={formatMoney(details.position_value)} />
        <DetailRow
          label="Комиссия"
          value={details.total_fees !== null ? `−${formatMoney(details.total_fees)}` : "—"}
          valueColor={details.total_fees !== null ? "var(--short)" : undefined}
          tooltip={
            details.status === "CLOSED" && details.entry_fee !== null && details.exit_fee !== null && details.total_fees !== null
              ? `вход ${formatMoney(details.entry_fee)} + выход ${formatMoney(details.exit_fee)} = ${formatMoney(details.total_fees)}`
              : undefined
          }
        />
        {details.realized_pnl !== null && (
          <DetailRow label="Валовый P&L" value={formatPnlMoney(details.realized_pnl)} valueColor={parseFloat(details.realized_pnl) >= 0 ? "var(--long)" : "var(--short)"} />
        )}
        <DetailRow
          label="Итоговый P&L"
          value={details.net_pnl !== null ? formatPnlMoney(details.net_pnl) : "—"}
          valueColor={details.net_pnl !== null ? (parseFloat(details.net_pnl) >= 0 ? "var(--long)" : "var(--short)") : undefined}
          tooltip={
            details.net_pnl !== null && details.realized_pnl !== null && details.total_fees !== null
              ? `${formatPnlMoney(details.realized_pnl)} − ${formatMoney(details.total_fees)} = ${formatPnlMoney(details.net_pnl)}`
              : undefined
          }
        />
      </DetailBlock>

      <DetailBlock title="Логика решения">
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
          <span style={{ font: "400 12.5px 'Golos Text',sans-serif", color: "var(--fg3)" }}>Вероятность</span>
          <span style={{ font: "700 16px 'JetBrains Mono',monospace" }}>{parseFloat(details.prediction_prob).toFixed(4)}</span>
        </div>
        <div style={{ position: "relative", height: 7, borderRadius: 4, background: "var(--surface3)", margin: "9px 0 4px", overflow: "hidden" }}>
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              height: "100%",
              width: `${Math.round(parseFloat(details.prediction_prob) * 100)}%`,
              background: details.direction === "LONG" ? "var(--long)" : "var(--short)",
            }}
          />
        </div>
        <DetailRow label="Направление" value={details.direction} valueColor={details.direction === "LONG" ? "var(--long)" : "var(--short)"} />
        <DetailRow label="Причина" value={details.entry_reason ?? "не записана"} valueColor={details.entry_reason ? undefined : "var(--warn)"} />
      </DetailBlock>

      <DetailBlock title="История">
        {details.history.length === 0 ? (
          <span style={{ font: "400 11px 'JetBrains Mono',monospace", color: "var(--fg3)" }}>Нет записей</span>
        ) : (
          details.history.map((h, i) => (
            <div key={i} style={{ display: "flex", gap: 10, padding: "3px 0", font: "400 11px 'JetBrains Mono',monospace" }}>
              <span style={{ color: "var(--fg3)", flex: "none" }}>{formatTime(h.ts, true)}</span>
              <span style={{ color: "var(--fg2)" }}>{h.text}</span>
            </div>
          ))
        )}
      </DetailBlock>
    </>
  );
}

export function DetailBlock({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ marginTop: 18, paddingTop: 13, borderTop: "1px solid var(--line)", font: "600 12.5px 'Golos Text',sans-serif", color: "var(--fg2)", marginBottom: 5 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

export function DetailRow({
  label,
  value,
  valueColor,
  tooltip,
}: {
  label: string;
  value: string;
  valueColor?: string;
  tooltip?: ReactNode;
}) {
  const labelSpan = (
    <span style={{ font: "400 12.5px 'Golos Text',sans-serif", color: "var(--fg3)" }}>{label}</span>
  );
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "5px 0" }}>
      {tooltip ? <HoverTip content={tooltip}>{labelSpan}</HoverTip> : labelSpan}
      <span style={{ font: "400 12px 'JetBrains Mono',monospace", color: valueColor ?? "var(--fg)" }}>{value}</span>
    </div>
  );
}
